import { Component, OnInit, Input, HostListener, DoCheck, HostBinding } from '@angular/core';
import { RemoteControlDeviceState } from '../../services/device-state-registry.service';
import { FeatureToggleService } from '@nx-fe/services';

@Component({
  selector: 'app-remote-control-extended-toolbar',
  templateUrl: './extended-toolbar.component.html',
  styleUrls: ['./extended-toolbar.component.scss'],
})
export class RemoteControlExtendedToolbarComponent implements OnInit, DoCheck {
  // Timeout period to show toolbar maximized on the activation
  public readonly showToolbarOnStartTimeout = 5000;

  @Input() isDisabled: boolean;
  @Input() rcState: RemoteControlDeviceState;
  @HostBinding('class.hideToolbar') get valid() {
    return !this.hasActiveChildToolbars;
  }
  // Tracks whether toolbar is maximized on the activation
  // for the specified timeout period
  public showToolbarWithTimeout = true;

  // Tracks state of the mouse (whether it's over the toolbar)
  public isMouseOver = false;

  // Tracks whether extender toolbar visibility changed
  // between subsequent change detection cycles
  private _wasVisible = false;

  private _toolbarMap = null;
  // Map of available toolbars with a predicate for toolbar activation
  // (makes toolbar visible)
  private get toolbarMap(): object {
    if (this._toolbarMap) {
      return this._toolbarMap;
    }

    this._toolbarMap = {
      annotation: () => this.rcState && this.rcState.annotations && this.rcState.annotations.isAnnotating,
    };

    return this._toolbarMap;
  }

  constructor(private featureToggleService: FeatureToggleService) {}

  // Tracks minimzation state (whether they request parent to stay maximized)
  // of each child toolbar
  public canMinimizeMap: { [key: string]: boolean } = {};

  // List of active toolbars
  public get toolbars() {
    const result = [];
    for (const [key, value] of Object.entries(this.toolbarMap)) {
      if (value()) {
        result.push(key);
      }
    }
    return result;
  }

  // Encapsulates conditions when extended toolbar should be maximized:
  // - when initial timeout not expired
  // - some child toolbar blocked minimition behavior
  // - maximize due to mouse being over the area of the toolbar
  public get showMaximizedToolbar(): boolean {
    return this.showToolbarWithTimeout || this.isMinimizationBlocked || this.isMouseOver;
  }

  // Indicates whether any of the child toolbars are currently active (visible)
  public get hasActiveChildToolbars() {
    return this.toolbars.length > 0;
  }

  // Indicates whether minimization is currently blocked by any of the
  // child toolbars
  public get isMinimizationBlocked(): boolean {
    return Object.values(this.canMinimizeMap).some(value => value === false);
  }

  public ngOnInit() {
    // Initialize default canMinimize values for all toolbars
    for (const [toolbarKey, _] of Object.entries(this.toolbarMap)) {
      this.canMinimizeMap[toolbarKey] = true;
    }
  }

  public ngDoCheck() {
    // Tracks visibility change and initiates toolbar show
    // behavior on start
    if (this.hasActiveChildToolbars && !this._wasVisible) {
      this.maximizeWithTimeout();
    }
    this._wasVisible = this.hasActiveChildToolbars;
  }

  public setCanMinimize(toolbar: string, value: boolean) {
    this.canMinimizeMap[toolbar] = value;
  }

  @HostListener('mouseenter')
  public onMouseEnter() {
    this.isMouseOver = true;
  }

  @HostListener('mouseleave')
  public onMousLeave() {
    this.isMouseOver = false;
  }

  private maximizeWithTimeout() {
    this.showToolbarWithTimeout = true;
    setTimeout(() => (this.showToolbarWithTimeout = false), this.showToolbarOnStartTimeout);
  }
}
